import React, { useEffect, useMemo, useRef, useState } from "react";
import Datetime from "react-datetime";
import moment from "moment";
import "react-datetime/css/react-datetime.css";
import { readStoredLabUser, useLab } from "../../../_helper/PharmacyProvider";
import {
  API_TIME_FORMAT,
  getMomentTimeFormat,
  getTimeFormatMaxLength,
  getTimeFormatPlaceholder,
  is12HourTimeFormat,
  maskTimeInput,
  resolvePickerTimeFormat,
  toApiTimeString,
} from "../../../utils/dateTime/timeFormat";
import "./TimePicker.css";

/**
 * Reusable TimePicker built on the existing `react-datetime` UI.
 *
 * Display format priority:
 * - Explicit `timeFormat` prop → logged-in user's `time_format` → utility default
 * - 12 Hours → display/entry like 07:30 PM
 * - 24 Hours → display/entry like 19:30
 * - Form/API value is always HH:mm (24-hour)
 */

/** Keep the dropdown visible inside scrollable tables and modals. */
const positionOpenTimePicker = (root) => {
  if (!root) return;
  const picker = root.querySelector(".rdtPicker");
  const input = root.querySelector("input");
  if (!picker || !input) return;

  const isOpen = Boolean(root.querySelector(".rdt.rdtOpen"));
  if (!isOpen) {
    picker.style.position = "";
    picker.style.top = "";
    picker.style.left = "";
    picker.style.zIndex = "";
    return;
  }

  const rect = input.getBoundingClientRect();
  const pickerHeight = picker.offsetHeight || 160;
  const spaceBelow = window.innerHeight - rect.bottom;
  const openAbove = spaceBelow < pickerHeight + 8 && rect.top > pickerHeight + 8;
  picker.style.position = "fixed";
  picker.style.zIndex = "200000";
  picker.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - 16 - rect.width))}px`;
  picker.style.top = openAbove
    ? `${Math.max(8, rect.top - pickerHeight - 4)}px`
    : `${rect.bottom + 4}px`;
};
const TimePicker = ({
  value = "",
  onChange,
  name,
  id,
  timeFormat,
  placeholder,
  className = "form-control",
  disabled = false,
  required = false,
  closeOnSelect = false,
  strictParsing = true,
  ...rest
}) => {
  const wrapperRef = useRef(null);
  const labContext = useLab() || {};
  const labUser = labContext.currentLab || readStoredLabUser();
  // Priority: explicit prop → logged-in user time_format → default
  const resolvedTimeFormat = resolvePickerTimeFormat(timeFormat, labUser);
  const momentTimeFormat = getMomentTimeFormat(resolvedTimeFormat);
  const resolvedPlaceholder =
    placeholder || getTimeFormatPlaceholder(resolvedTimeFormat);
  const inputMaxLength = getTimeFormatMaxLength(resolvedTimeFormat);

  // Controlled value from parent/forms is always API HH:mm.
  const selectedMoment = useMemo(() => {
    if (!value) return null;
    const parsed = moment(value, API_TIME_FORMAT, true);
    return parsed.isValid() ? parsed : null;
  }, [value]);

  const [draftText, setDraftText] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return undefined;

    const place = () => positionOpenTimePicker(root);
    const observer = new MutationObserver(place);
    observer.observe(root, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class"],
    });
    window.addEventListener("resize", place);
    document.addEventListener("scroll", place, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", place);
      document.removeEventListener("scroll", place, true);
    };
  }, []);

  const displayText = useMemo(() => {
    if (isEditing) return draftText;
    if (!selectedMoment) return "";
    // Keep display formatting aligned with react-datetime/moment UI.
    return selectedMoment.format(momentTimeFormat);
  }, [isEditing, draftText, selectedMoment, momentTimeFormat]);

  const emitApiValue = (apiValue) => {
    if (!onChange) return;
    onChange({
      target: {
        name: name || "",
        value: apiValue || "",
      },
    });
  };

  const commitDisplayText = (text, { allowEmpty = true } = {}) => {
    const trimmed = String(text || "").trim();
    if (!trimmed) {
      if (allowEmpty) {
        setDraftText("");
        emitApiValue("");
      }
      return true;
    }

    const apiValue = toApiTimeString(trimmed, resolvedTimeFormat);
    if (!apiValue) return false;

    const apiMoment = moment(apiValue, API_TIME_FORMAT, true);
    setDraftText(apiMoment.format(momentTimeFormat));
    emitApiValue(apiValue);
    return true;
  };

  const handlePickerChange = (next) => {
    if (!moment.isMoment(next) || !next.isValid()) return;
    // Picker selection → always API HH:mm
    const apiValue = next.format(API_TIME_FORMAT);
    setIsEditing(false);
    setDraftText(next.format(momentTimeFormat));
    emitApiValue(apiValue);
  };

  const handleInputKeyDown = (event, props) => {
    const allowedControl = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Home",
      "End",
    ];
    const isDigit = /[0-9]/.test(event.key);
    const isAmPmChar =
      is12HourTimeFormat(resolvedTimeFormat) && /[aApPmM ]/.test(event.key);

    if (!isDigit && !isAmPmChar && !allowedControl.includes(event.key)) {
      event.preventDefault();
    }
    props.onKeyDown?.(event);
  };

  const handleInputChange = (event, props) => {
    const nextText = maskTimeInput(
      event.target.value,
      resolvedTimeFormat,
      draftText || displayText
    );
    setIsEditing(true);
    setDraftText(nextText);

    props.onChange?.({
      ...event,
      target: {
        ...event.target,
        value: nextText,
      },
    });

    if (!nextText.trim()) {
      emitApiValue("");
      return;
    }

    // Manual entry → convert to API HH:mm only when complete/valid.
    const apiValue = toApiTimeString(nextText.trim(), resolvedTimeFormat);
    if (apiValue) {
      emitApiValue(apiValue);
    }
  };

  const handleInputFocus = (event, props) => {
    setIsEditing(true);
    setDraftText(displayText);
    props.onFocus?.(event);
  };

  const handleInputBlur = (event, props) => {
    const committed = commitDisplayText(draftText || event.target.value, {
      allowEmpty: true,
    });
    if (!committed) {
      setDraftText(selectedMoment ? selectedMoment.format(momentTimeFormat) : "");
    }
    setIsEditing(false);
    props.onBlur?.(event);
  };

  return (
    <div ref={wrapperRef} className="cd-lab-timepicker">
    <Datetime
      dateFormat={false}
      timeFormat={momentTimeFormat}
      value={selectedMoment || ""}
      onChange={handlePickerChange}
      closeOnSelect={closeOnSelect}
      strictParsing={strictParsing}
      inputProps={{
        id: id || name,
        name,
        className,
        disabled,
        required,
        placeholder: resolvedPlaceholder,
        autoComplete: "off",
      }}
      renderInput={(props) => (
        <input
          {...props}
          id={id || name}
          name={name}
          className={className}
          placeholder={resolvedPlaceholder}
          value={displayText}
          disabled={disabled}
          required={required}
          maxLength={inputMaxLength}
          onChange={(event) => handleInputChange(event, props)}
          onKeyDown={(event) => handleInputKeyDown(event, props)}
          onFocus={(event) => handleInputFocus(event, props)}
          onBlur={(event) => handleInputBlur(event, props)}
        />
      )}
      {...rest}
    />
    </div>
  );
};

export { API_TIME_FORMAT, toApiTimeString };
export default TimePicker;
